import { createColumnHelper, type ColumnHelper } from "@tanstack/react-table";

import Flex from "../../../../../../components/Flex";
import { FieldLabel } from "../../../../../../components/FieldLabel";

import { type ReactNode, Fragment, isValidElement } from "react";

import {
  ColumnDef,
  ColumnHeader,
  ColumnType,
  ColumnTypeConfig,
  EditMode,
  TableRow,
} from "../../../types";

import { TableEmptyCellContent } from "../../../base/cells/TableEmptyCellContent";
import { CellStatus } from "../cells/CellStatus";
import {
  getColumnTypeDefaults,
  resolveColumnTypeConfig,
} from "../../../columnTypeDefaults";

// Extend TanStack Table's ColumnMeta type to include our custom properties
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    editMode?: EditMode;
    editConfig?: ColumnDef<TData>["editConfig"];
    /**
     * The original column definition, stored for type narrowing and runtime access
     */
    columnDef?: ColumnDef<TData>;
    /**
     * Whether the header action (expand all / select all) is disabled for this column
     */
    disableHeaderAction?: boolean;
    /**
     * Resolved header alignment for DataTable-owned header layout.
     */
    headerAlign?: HeaderAlign;
    /**
     * Whether this column renders a grouped header.
     */
    isGroupHeader?: boolean;
  }
}

/**
 * Props for the getTanStackColumnDef function
 */
export type TanStackColumnDefProps<T> = {
  /**
   * The column definition to convert to a TanStack column definition
   */
  columnDef: ColumnDef<T>;
  /**
   * Table-level default content for empty cells. Column-level `emptyCellContent`
   * takes precedence over this value.
   */
  emptyCellContent?: ReactNode;
};

type TanStackColumnDef<T extends object> =
  | ReturnType<ColumnHelper<T>["group"]>
  | ReturnType<ColumnHelper<T>["accessor"]>;

type HeaderAlign = "start" | "center" | "end";

type HeaderJustifyContent = "flex-start" | "center" | "flex-end";

type NormalizedColumnHeader = ColumnHeader & {
  label: string;
};

function resolveHeaderAlign<T extends object>(
  align?: ColumnDef<T>["align"],
  isGroup = false,
): HeaderAlign {
  if (isGroup) {
    return "start";
  }

  return align ?? "start";
}

function getHeaderJustifyContent(align: HeaderAlign): HeaderJustifyContent {
  if (align === "center") {
    return "center";
  }

  if (align === "end") {
    return "flex-end";
  }

  return "flex-start";
}

function normalizeColumnHeader<T extends object>(
  columnDef: ColumnDef<T>,
): NormalizedColumnHeader {
  return {
    label:
      columnDef.header?.label ?? columnDef.headerLabel ?? String(columnDef.id),
    required: columnDef.header?.required,
    moreInfo: columnDef.header?.moreInfo,
  };
}

function renderColumnHeader<T extends object>(
  header: NormalizedColumnHeader,
  {
    align,
    isGroup = false,
  }: {
    align?: ColumnDef<T>["align"];
    isGroup?: boolean;
  },
) {
  const resolvedAlign = resolveHeaderAlign(align, isGroup);

  return (
    <div
      data-a2-datatable-header-wrapper="true"
      data-a2-datatable-header-align={resolvedAlign}
      data-a2-datatable-header-kind={isGroup ? "group" : "leaf"}
      style={{
        justifyContent: getHeaderJustifyContent(resolvedAlign),
        maxWidth: "100%",
        width: "fit-content",
      }}
    >
      <div
        data-a2-datatable-header-content="true"
        style={{ maxWidth: "100%", width: "fit-content" }}
      >
        <FieldLabel
          el="div"
          required={header.required}
          moreInfo={header.moreInfo}
          style={{
            maxWidth: "100%",
            textAlign: resolvedAlign,
            width: "fit-content",
          }}
        >
          {header.label}
        </FieldLabel>
      </div>
    </div>
  );
}
function renderRawCellValue<TValue>(value: TValue): ReactNode {
  if (value === null || value === undefined) {
    return null;
  }

  if (value === "") {
    return "";
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "bigint"
  ) {
    return value;
  }

  if (typeof value === "boolean") {
    return String(value);
  }

  if (typeof value === "symbol") {
    return value.toString();
  }

  if (typeof value === "object" && isValidElement(value)) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item, index) => (
      <Fragment key={index}>{renderRawCellValue(item)}</Fragment>
    ));
  }

  return String(value);
}

/**
 * Build the column definition for TanStack useReactTable.
 * This converts our ColumnDef<T> to TanStack's column definition format,
 * preserving the original column definition in meta for type narrowing.
 *
 * @param props - The props for the getTanStackColumnDef function
 * @param props.columnDef - The column definition to convert
 * @returns The column definition for TanStack useReactTable
 */
export function getTanStackColumnDef<T extends object>({
  columnDef,
  emptyCellContent,
}: TanStackColumnDefProps<T>): TanStackColumnDef<T> {
  const columnHelper = createColumnHelper<T>();
  const normalizedHeader = normalizeColumnHeader(columnDef);

  // If this column has sub-columns, create a group column
  if (columnDef.columns && columnDef.columns.length > 0) {
    return columnHelper.group({
      id: columnDef.id as string,
      header: () =>
        renderColumnHeader(normalizedHeader, {
          align: columnDef.align,
          isGroup: true,
        }),
      columns: columnDef.columns.map((column) =>
        getTanStackColumnDef({
          columnDef: column,
          emptyCellContent,
        }),
      ),
      meta: {
        columnDef,
        headerAlign: resolveHeaderAlign(columnDef.align, true),
        isGroupHeader: true,
      },
      footer: columnDef.footerContent
        ? () => columnDef.footerContent
        : undefined,
      enableResizing: columnDef.resizable,
    });
  }

  // Resolve type defaults if type is specified
  let resolvedAlign = columnDef.align;
  let resolvedRenderCell = columnDef.renderCell;

  if (columnDef.type) {
    const [columnType, typeOptions] = resolveColumnTypeConfig(
      columnDef.type as ColumnTypeConfig<ColumnType>,
    );
    const typeDefaults = getColumnTypeDefaults(columnType);

    // Apply type defaults if not explicitly overridden
    if (resolvedAlign === undefined) {
      resolvedAlign = typeDefaults.align;
    }
    if (resolvedRenderCell === undefined) {
      resolvedRenderCell = typeDefaults.createRenderCell(
        typeOptions,
      ) as typeof resolvedRenderCell;
    }
  }

  const resolvedEmptyContent = columnDef.emptyCellContent ??
    emptyCellContent ?? <TableEmptyCellContent />;

  const metaColumnDef = {
    ...columnDef,
    headerLabel: normalizedHeader.label,
  };

  // Otherwise, create an accessor column
  // The accessor preserves the type T[columnDef.id] as the value type,
  // though TanStack Table's Cell type doesn't expose this at the table level
  return columnHelper.accessor((row: T) => row[columnDef.id], {
    id: columnDef.id as string,
    cell: (info) => {
      const rawCellValue = info.row.original[columnDef.id];
      let cellContent;
      if (resolvedRenderCell) {
        cellContent = resolvedRenderCell(rawCellValue, {
          row: info.row.original,
          depth: info.row.depth,
        });
      } else {
        cellContent = renderRawCellValue(rawCellValue);
      }
      if (cellContent === "") {
        cellContent = resolvedEmptyContent;
      }

      const meta = (info.row.original as TableRow<T>).meta;
      const cellError = meta?.errors?.[columnDef.id as keyof T];
      const cellWarning = meta?.warnings?.[columnDef.id as keyof T];

      return (
        <Flex
          justifyContent={resolvedAlign}
          alignItems="center"
          wrap="wrap"
          gap="1"
          style={{
            width: "100%",
            textAlign: resolvedAlign,
          }}
        >
          {cellError ? (
            <CellStatus status={cellError} variant="error" />
          ) : cellWarning ? (
            <CellStatus status={cellWarning} variant="warning" />
          ) : null}
          {cellContent ?? resolvedEmptyContent}
        </Flex>
      );
    },
    header: () =>
      renderColumnHeader(normalizedHeader, {
        align: resolvedAlign,
      }),
    footer: columnDef.footerContent ? () => columnDef.footerContent : undefined,
    minSize: columnDef.minWidth,
    maxSize: columnDef.maxWidth,
    enableResizing: columnDef.resizable,
    enableSorting: !!columnDef.sortable,
    meta: {
      editMode: columnDef.editConfig?.mode,
      editConfig: columnDef.editConfig,
      columnDef: metaColumnDef, // Store original for type narrowing
      headerAlign: resolveHeaderAlign(resolvedAlign),
      isGroupHeader: false,
    },

    // If custom sorting function is provided, use it
    ...(columnDef.sortable && typeof columnDef.sortable === "function"
      ? {
          sortingFn: (rowA, rowB, colId) => {
            if (typeof columnDef.sortable === "function") {
              return columnDef.sortable(
                rowA.getValue(colId),
                rowB.getValue(colId),
              );
            }
            return 0;
          },
        }
      : undefined),
  });
}
